import { Col, Collapse, Flex, Row, Typography } from 'antd';
import { useEffect, useState } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { useFetchBoardViewerConfig } from 'business/quality-control/hooks/use-fetch-board-viewer-config';
import {
  HeaderType,
  ReportHeader,
} from 'business/quality-control/pages/defect-report-history/components/defect-report-details-drawer/components/report-header';
import { DefectReportHistory } from 'business/quality-control/pages/defect-report-history/components/defect-report-table';
import { RingBoardViewer } from 'business/quality-control/pages/defect-report-history/components/ring-board-viewer';
import { YesNoTag } from 'business/quality-control/pages/defect-report-history/components/yes-no-tag';
import { useGetDefectReportPicturesData } from 'business/quality-control/pages/defect-report-history/hooks/data/get-defect-report-pictures';
import { DefectReportStatus } from 'business/quality-control/pages/defect-report-history/hooks/data/types';
import { useGetDefectIndicatorData } from 'business/quality-control/pages/record-segment-defect/hooks/data/get-defect-indicator';
import { formatDateWithMiddleDot } from 'technical/format/date';
import { Picture, PicturesList } from 'ui/pictures';

const { Text } = Typography;

export const DefectReportDetailsDefect = ({
  report,
}: {
  report: DefectReportHistory;
}) => {
  const { t } = useTranslation();
  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site id');

  const [activeKeys, setActiveKeys] = useState<string[]>(
    report.repairId === null ? ['1'] : [],
  );

  const { data: picturesData, isLoading } = useGetDefectReportPicturesData(
    report.id,
  );

  const { hasIndicators } = useGetDefectIndicatorData(
    currentConstructionSiteId,
  );
  const fieldSpan = hasIndicators ? 6 : 8;

  const {
    segmentDrawConfigs,
    segmentWidth,
    tunnelPositions,
    defectReportTypes,
    isLoading: isLoadingBoardConfig,
    error: boardConfigError,
  } = useFetchBoardViewerConfig(currentConstructionSiteId);
  const defectPictures: Picture[] = picturesData
    ? picturesData?.pictures.map((item) => ({
        description: item.id,
        path: item.path,
      }))
    : [];

  useEffect(() => {
    setActiveKeys(report.repairId === null ? ['1'] : []);
  }, [report.repairId]);

  return (
    <Collapse
      activeKey={activeKeys}
      onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
      expandIconPosition="end"
      style={{ background: '#fff' }}
      items={[
        {
          key: '1',
          label: (
            <ReportHeader
              status={report.status as DefectReportStatus}
              type={HeaderType.defect}
            />
          ),
          children: (
            <>
              <RingBoardViewer
                defectId={report.id}
                ringNumber={report.ringNumber}
                segmentDrawConfigs={segmentDrawConfigs}
                segmentWidth={segmentWidth}
                tunnelPositions={tunnelPositions}
                defectReportTypes={defectReportTypes}
                isLoadingBoardConfig={isLoadingBoardConfig}
                boardConfigError={boardConfigError}
              />

              <Row gutter={[16, 16]}>
                <Col span={fieldSpan}>
                  <Flex vertical>
                    <Text type="secondary">
                      {t('qualityControl.defectReportHistory.defectType')}
                    </Text>
                    <Text strong>{report.defectType}</Text>
                  </Flex>
                </Col>
                <Col span={fieldSpan}>
                  <Flex vertical>
                    <Text type="secondary">
                      {t('qualityControl.defectReportHistory.toRepair')}
                    </Text>
                    <div>
                      <YesNoTag value={report.toRepair} />
                    </div>
                  </Flex>
                </Col>
                {hasIndicators ? (
                  <Col span={fieldSpan}>
                    <Flex vertical>
                      <Text type="secondary">
                        {t('qualityControl.defectReportHistory.indicator')}
                      </Text>
                      <Text strong>{report.indicator ?? '-'}</Text>
                    </Flex>
                  </Col>
                ) : null}
                <Col span={fieldSpan}>
                  <Flex vertical>
                    <Text type="secondary">
                      {t('qualityControl.defectReportHistory.reportDate')}
                    </Text>
                    <Text strong>
                      {formatDateWithMiddleDot(report.reportDate)}
                    </Text>
                  </Flex>
                </Col>
              </Row>

              <Flex vertical style={{ marginTop: '20px' }}>
                <Text type="secondary">
                  {t('qualityControl.defectReportHistory.comment')}
                </Text>
                <Text>{report.defectDescription || '-'}</Text>
              </Flex>

              <Flex vertical style={{ marginTop: '20px' }}>
                <Text type="secondary">
                  {t('qualityControl.defectReportHistory.author')}
                </Text>
                <Text strong>{report.defectAuthor}</Text>
              </Flex>

              <Flex vertical style={{ marginTop: '20px' }}>
                <PicturesList pictures={defectPictures} isLoading={isLoading} />
              </Flex>
            </>
          ),
        },
      ]}
    />
  );
};
